import { ApiError, generateHandbook, resendHandbookEmail } from '../src/api/client.ts';
import { QUESTIONS, SECTIONS } from './questions.js';

let submissionPending = false;
let resendPending = false;
const hasValue = (value) => (Array.isArray(value) ? value.length > 0 : String(value || '').trim() !== '');
const renderAnswer = (value) => (Array.isArray(value) ? value.join(', ') : value);

function buildReviewMarkup(state) {
  const sections = SECTIONS.map((section, index) => {
    const items = QUESTIONS.filter((question) => question.section === section.id && hasValue(state.answers[question.id]));
    if (!items.length) return '';
    return `
      <section class="handbook-section">
        <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;">
          <h3>${section.title}</h3>
          <button class="btn btn-text" data-edit-step="${index}">Edit</button>
        </div>
        <ul>${items.map((question) => `<li><strong>${question.question}:</strong> ${renderAnswer(state.answers[question.id])}</li>`).join('')}</ul>
      </section>`;
  }).join('');
  return `
    <div class="complete-screen" style="display:flex;">
      <div class="complete-icon">✓</div>
      <h2>Review your draft</h2>
      <p>One handbook per purchase — review your answers carefully before generating.</p>
      ${state.reviewNotice ? `<p style="color:var(--danger);">${state.reviewNotice}</p>` : ''}
      <div style="width:100%;text-align:left;">${sections}</div>
      <div style="display:flex;gap:12px;width:100%;">
        <button class="btn btn-secondary" id="btn-review-back" style="flex:1;">Back to questionnaire</button>
        <button class="btn btn-accent" id="btn-generate-handbook" style="flex:2;" ${submissionPending ? 'disabled' : ''}>${submissionPending ? 'Generating...' : 'Generate & email my handbook'}</button>
      </div>
      ${state.reviewCanResend ? '<button class="btn btn-secondary" id="btn-review-resend">Show resend options</button>' : ''}
    </div>`;
}

function buildDoneMarkup(state) {
  return `
    <div class="complete-screen" style="display:flex;">
      <div class="complete-icon">✓</div>
      <h2>Check your email</h2>
      <p>${state.doneNotice || 'Your handbook has been prepared for email delivery.'}</p>
      <p>Need changes? Edits require a new handbook purchase.</p>
      <div style="display:flex;gap:12px;width:100%;">
        <button class="btn btn-secondary" id="btn-resend-handbook" ${resendPending ? 'disabled' : ''}>${resendPending ? 'Sending...' : 'Resend'}</button>
        <button class="btn btn-accent" id="btn-purchase-again">Back to dashboard</button>
      </div>
    </div>`;
}

async function handleGenerate(ctx) {
  if (submissionPending) return;
  submissionPending = true;
  ctx.renderReviewScreen('review');
  try {
    await generateHandbook(ctx.state.childId, ctx.state.productId, ctx.state.answers);
    ctx.state.entitlement = { ...(ctx.state.entitlement ?? {}), emailedAt: new Date().toISOString(), status: 'fulfilled' };
    ctx.state.doneNotice = 'Check your email — your handbook PDF is on its way.';
    ctx.state.reviewCanResend = false;
    ctx.state.reviewNotice = '';
    ctx.state.screenIntent = 'done';
    ctx.routeScreen();
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      ctx.state.entitlement = { ...(ctx.state.entitlement ?? {}), status: 'none' };
      ctx.state.screenIntent = 'pay';
      ctx.routeScreen();
      return;
    }
    if (error instanceof ApiError && error.status === 502) {
      ctx.state.entitlement = { ...(ctx.state.entitlement ?? {}), emailedAt: null, status: 'fulfilled' };
      ctx.state.doneNotice = 'Generation succeeded, but delivery failed. Use Resend to try the email again.';
      ctx.state.reviewCanResend = true;
      ctx.state.screenIntent = 'done';
      ctx.routeScreen();
      return;
    }
    ctx.state.reviewNotice = error instanceof Error ? error.message : 'Unable to generate your handbook right now.';
    ctx.renderReviewScreen('review');
  } finally {
    submissionPending = false;
  }
}

async function handleResend(ctx) {
  if (resendPending) return;
  resendPending = true;
  ctx.renderReviewScreen('done');
  try {
    await resendHandbookEmail(ctx.state.childId, ctx.state.productId);
    ctx.state.doneNotice = 'A new copy of your handbook is on its way to your inbox.';
    ctx.state.entitlement = { ...(ctx.state.entitlement ?? {}), emailedAt: new Date().toISOString(), status: 'fulfilled' };
    ctx.routeScreen();
  } catch (error) {
    ctx.state.doneNotice = error instanceof Error ? error.message : 'We could not resend the handbook right now.';
    ctx.renderReviewScreen('done');
  } finally {
    resendPending = false;
  }
}

export function renderReviewScreen(mode, ctx) {
  const target = mode === 'review' ? ctx.elements.screenReview : ctx.elements.screenDone;
  target.innerHTML = mode === 'review' ? buildReviewMarkup(ctx.state) : buildDoneMarkup(ctx.state);
  if (mode === 'review') {
    target.querySelector('#btn-review-back')?.addEventListener('click', () => {
      ctx.state.screenIntent = 'wizard';
      ctx.state.currentStep = SECTIONS.length - 1;
      ctx.routeScreen();
    });
    target.querySelector('#btn-generate-handbook')?.addEventListener('click', () => void handleGenerate(ctx));
    target.querySelector('#btn-review-resend')?.addEventListener('click', () => {
      ctx.state.screenIntent = 'done';
      ctx.routeScreen();
    });
    target.querySelectorAll('[data-edit-step]').forEach((button) => {
      button.addEventListener('click', () => ctx.goToStep(Number(button.getAttribute('data-edit-step'))));
    });
    return;
  }
  target.querySelector('#btn-resend-handbook')?.addEventListener('click', () => void handleResend(ctx));
  target.querySelector('#btn-purchase-again')?.addEventListener('click', () => {
    ctx.state.childId = null;
    ctx.state.productId = null;
    ctx.state.entitlement = null;
    ctx.state.screenIntent = 'dashboard';
    ctx.routeScreen();
  });
}
